/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { clickTrack } from '@/utils/analytics/clickTrack';
import { analyticsGlobalVariables } from '@/utils/analytics/dataLayer/analyticsGlobalVariables';
import { deleteCookie } from '@/utils/cookie';
import { useHeaderData } from '@/features/header/api/getHeader';
import root from 'window-or-global';
import { replaceHistoryStateOptionsOnClick } from '@/features/common/utils/replaceHistoryStateOptionsOnClick';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { sessionStorage } from '@/utils/sessionStorage';
import { useSetPreferredStoreMutation } from '../api/postPreferredStore';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { setCookieSync } from '@/utils/cookie';
import { useEffect } from 'react';
import { crashReporter } from '@/utils/crashReporter';
import { useAppData } from '@/hooks/useAppData';

const STORE_ERROR_MESSAGE = 'Error setting preferred store in useSetStore';
const MUTATION_ERROR_TYPE = 'mutation-error';

const trackSetStore = (selectedStoreId: string, searchedInput?: string) => {
  const nearbyStoreFlowType = String(sessionStorage.getItem('sister_store_update_type'));
  if (
    nearbyStoreFlowType === 'select_store_and_add_to_cart' ||
    nearbyStoreFlowType === 'select_store_and_update_cart'
  ) {
    return;
  }

  clickTrack({
    eventType: 'setStoreGlobalHeader',
    storeId: selectedStoreId,
    storeSearchTerm: searchedInput,
  });
};

export function useSetStore(closeStoreModal: () => void): {
  setStore: (
    storeNumber: string,
    zipCode: string,
    _isNearByStoreSelected: boolean,
    searchedInput?: string,
  ) => Promise<void>;
  isPending: boolean;
} {
  const { data: storeDetails } = useStoreDetailsData();
  const currentStore = storeDetails?.storeNumber ?? '';
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const router = useLegacyRouter();
  const { refetch: refetchHeaderData } = useHeaderData();
  const { mutateAsync: setStore, isPending } = useSetPreferredStoreMutation();

  useEffect(() => {
    if (router.query.loadMore) {
      sessionStorage.setItem('previousStore', currentStore);
    }
  }, [router.query.loadMore, currentStore]);

  const dispatchSetStoreNumber = async (storeNumber: string) => {
    await setStore({ storeNumber });
    await refetchHeaderData({ throwOnError: true }); // Wait for header refresh and surface errors to catch to complete before closing modal to prevent 499 errors

    replaceHistoryStateOptionsOnClick(
      historyStateOptionAttributes.latestSelectedPageYOffset,
      root.pageYOffset,
    );
  };

  const setStoreFunction = async (
    storeNumber: string,
    zipCode: string,
    _isNearByStoreSelected: boolean,
    searchedInput?: string,
  ) => {
    try {
      await dispatchSetStoreNumber(storeNumber);

      /* Analytics Start */
      trackSetStore(storeNumber, searchedInput);
      await deleteCookie('preferredZipCode');
      await deleteCookie('preferredStoreId');

      setCookieSync('preferredZipCode', zipCode);
      setCookieSync('preferredStoreId', storeNumber);
      analyticsGlobalVariables({ userAuthenticated });
      /* Analytics End */

      closeStoreModal();
    } catch (error) {
      void crashReporter(STORE_ERROR_MESSAGE, MUTATION_ERROR_TYPE, error, 'error');
      closeStoreModal();
    }
  };

  return { setStore: setStoreFunction, isPending };
}
